/**
 * M3U Playlist Parser
 * Parses M3U/M3U8 files and extracts stream information
 */
/**
 * Parse M3U file content and extract stream information
 * @param content - The text content of the M3U file
 * @returns Array of parsed streams
 */
export function parseM3U(content) {
    const streams = [];
    const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    let currentTitle = '';
    let currentDuration;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        // Skip the header
        if (line === '#EXTM3U') {
            continue;
        }
        // Parse EXTINF lines (metadata)
        if (line.startsWith('#EXTINF:')) {
            // Format: #EXTINF:duration,title (simple)
            // Extended format: #EXTINF:duration tvg-id="..." tvg-name="..." group-title="...",Title
            // Note: Extended format has NO COMMA after duration, just a space
            // First, extract everything after #EXTINF:
            const content = line.substring(8); // Remove '#EXTINF:'
            // Try to match duration at the start
            const durationMatch = content.match(/^(-?\d+(?:\.\d+)?)/);
            if (durationMatch) {
                const duration = parseInt(durationMatch[1]);
                currentDuration = duration > 0 ? duration : undefined;
                // Get everything after the duration
                const afterDuration = content.substring(durationMatch[1].length).trim();
                // Find the last comma - the title is after it
                const lastCommaIndex = afterDuration.lastIndexOf(',');
                if (lastCommaIndex >= 0) {
                    // Title is after the last comma
                    currentTitle = afterDuration.substring(lastCommaIndex + 1).trim();
                }
                else {
                    // No comma found, use whole thing as title (shouldn't happen)
                    currentTitle = afterDuration;
                }
            }
            continue;
        }
        // Skip other comments
        if (line.startsWith('#')) {
            continue;
        }
        // This is a URL line
        if (line.startsWith('http://') || line.startsWith('https://')) {
            streams.push({
                title: currentTitle || extractTitleFromUrl(line),
                url: line,
                duration: currentDuration
            });
            // Reset for next entry
            currentTitle = '';
            currentDuration = undefined;
        }
    }
    return streams;
}
/**
 * Extract a readable title from a URL
 * @param url - The stream URL
 * @returns A formatted title
 */
function extractTitleFromUrl(url) {
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        // Get the last part of the path
        const parts = pathname.split('/').filter(p => p);
        const lastPart = parts[parts.length - 1] || 'Stream';
        // Remove file extension and format
        return lastPart
            .replace(/\.(m3u8?|ts|mp4)$/i, '')
            .replace(/[-_]/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
    }
    catch (e) {
        return 'Stream';
    }
}
/**
 * Validate if a URL is a valid stream URL
 * @param url - URL to validate
 * @returns True if valid stream URL
 */
export function isValidStreamUrl(url) {
    try {
        const urlObj = new URL(url);
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    }
    catch (e) {
        return false;
    }
}
/**
 * Parse a single stream URL and create a stream object
 * @param url - The stream URL
 * @param title - Optional custom title
 * @returns Parsed stream object
 */
export function parseSingleStream(url, title) {
    if (!isValidStreamUrl(url)) {
        return null;
    }
    return {
        title: title || extractTitleFromUrl(url),
        url: url.trim(),
        duration: undefined // No duration for single URLs
    };
}
